// @catalog F.006
// @catalog F.030
// Helpers compartidos para filtro por nombre + columnas sortables en tablas
// estilo handoff. Complementa TableCells (que aporta TableShell/HeaderCell/BodyCell).
//
// - TableFilterInput: TextField MUI con search icon + clear button.
// - SortableHeaderCell: HeaderCell clickeable con icono de sort (▲/▼ MUI).
// - useTableControls<T>: hook que devuelve filter + sort state + processedData
//   ya filtrada/ordenada según accessors declarados por la tabla.

import { useMemo, useState, type ReactNode } from "react";
import { Box, TextField, InputAdornment, Tooltip } from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ArrowDropUp,
  ArrowDropDown,
  UnfoldMore,
} from "@mui/icons-material";
import { HeaderCell } from "./TableCells";

// ─────────────────────────────────────────────────────────────────────────────
// Tipos públicos
// ─────────────────────────────────────────────────────────────────────────────
export type SortDir = "asc" | "desc";

export interface TableSortState {
  key: string | null;
  dir: SortDir;
}

// ─────────────────────────────────────────────────────────────────────────────
// Icono visual de sort
// ─────────────────────────────────────────────────────────────────────────────
function SortIcon({
  active,
  dir,
}: {
  active: boolean;
  dir: SortDir;
}): JSX.Element {
  if (!active) {
    return (
      <UnfoldMore
        sx={{
          fontSize: 14,
          color: "var(--ink-4)",
          opacity: 0.6,
          marginLeft: "2px",
        }}
      />
    );
  }
  return dir === "asc" ? (
    <ArrowDropUp
      sx={{ fontSize: 18, color: "var(--ink-1)", marginLeft: "-2px" }}
    />
  ) : (
    <ArrowDropDown
      sx={{ fontSize: 18, color: "var(--ink-1)", marginLeft: "-2px" }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SortableHeaderCell — header clickeable con icono
// ─────────────────────────────────────────────────────────────────────────────
interface SortableHeaderCellProps {
  sortKey: string;
  currentSort: TableSortState;
  onSort: (key: string) => void;
  align?: "left" | "right" | "center";
  width?: string | number;
  // Texto de ayuda mostrado al hacer hover/focus sobre el header (MUI Tooltip).
  // Útil para explicar contra qué se calcula una métrica (ej. base del crecimiento).
  tooltip?: ReactNode;
  children: ReactNode;
}

export function SortableHeaderCell(
  props: SortableHeaderCellProps,
): JSX.Element {
  const {
    sortKey,
    currentSort,
    onSort,
    align = "left",
    width,
    tooltip,
    children,
  } = props;
  const active = currentSort.key === sortKey;
  const button = (
    <Box
      component="button"
      type="button"
      onClick={() => onSort(sortKey)}
      sx={{
        all: "unset",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        gap: "2px",
        width: "100%",
        fontWeight: 500,
        fontSize: "11.5px",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: active ? "var(--ink-1)" : "var(--ink-3)",
        transition: "color 120ms",
        "&:hover": { color: "var(--ink-1)" },
        "&:focus-visible": {
          outline: "2px solid var(--accent)",
          outlineOffset: "2px",
          borderRadius: "2px",
        },
      }}
      aria-sort={active ? (currentSort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      {children}
      <SortIcon active={active} dir={currentSort.dir} />
    </Box>
  );
  return (
    <HeaderCell align={align} {...(width !== undefined && { width })}>
      {tooltip ? (
        <Tooltip title={tooltip} arrow placement="top">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </HeaderCell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TableFilterInput — search por nombre
// ─────────────────────────────────────────────────────────────────────────────
interface TableFilterInputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  // Etiqueta auxiliar a la derecha (ej. "12 resultados de 47")
  helper?: string;
}

export function TableFilterInput(props: TableFilterInputProps): JSX.Element {
  const { value, onChange, placeholder = "Buscar por nombre…", helper } = props;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
      }}
    >
      <TextField
        size="small"
        variant="outlined"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        sx={{
          width: { xs: "100%", sm: 280 },
          "& .MuiOutlinedInput-root": {
            fontSize: "13px",
            backgroundColor: "var(--surface)",
            "& fieldset": { borderColor: "var(--border)" },
            "&:hover fieldset": { borderColor: "var(--ink-4)" },
          },
          "& .MuiOutlinedInput-input": {
            padding: "7px 10px 7px 0",
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 16, color: "var(--ink-3)" }} />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <Box
                component="button"
                type="button"
                onClick={() => onChange("")}
                aria-label="Limpiar filtro"
                sx={{
                  all: "unset",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  color: "var(--ink-3)",
                  "&:hover": { color: "var(--ink-1)" },
                }}
              >
                <ClearIcon sx={{ fontSize: 16 }} />
              </Box>
            </InputAdornment>
          ) : undefined,
        }}
      />
      {helper && (
        <Box
          component="span"
          sx={{ fontSize: "11.5px", color: "var(--ink-3)" }}
        >
          {helper}
        </Box>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// useTableControls<T> — state + processedData (filter + sort)
// ─────────────────────────────────────────────────────────────────────────────
type Accessor<T> = (item: T) => string | number | null | undefined;

interface UseTableControlsOptions<T> {
  data: T[];
  // Función que devuelve el texto contra el que filtra el input.
  filterAccessor: (item: T) => string;
  // Accessors por sortKey. Tipo del valor decide si compara numérico o string.
  sortAccessors: Record<string, Accessor<T>>;
  // Sort inicial.
  defaultSort?: TableSortState;
}

interface UseTableControlsResult<T> {
  filter: string;
  setFilter: (next: string) => void;
  sort: TableSortState;
  toggleSort: (key: string) => void;
  processedData: T[];
}

export function useTableControls<T>(
  options: UseTableControlsOptions<T>,
): UseTableControlsResult<T> {
  const { data, filterAccessor, sortAccessors, defaultSort } = options;
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<TableSortState>(
    defaultSort ?? { key: null, dir: "desc" },
  );

  const toggleSort = (key: string): void => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "desc" };
      return { key, dir: prev.dir === "desc" ? "asc" : "desc" };
    });
  };

  const processedData = useMemo<T[]>(() => {
    const trimmed = filter.trim().toLowerCase();
    let result = data;
    if (trimmed.length > 0) {
      result = result.filter((item) =>
        filterAccessor(item).toLowerCase().includes(trimmed),
      );
    }
    if (sort.key) {
      const acc = sortAccessors[sort.key];
      if (acc) {
        result = [...result].sort((a, b) => {
          const av = acc(a);
          const bv = acc(b);
          const aNum = typeof av === "number";
          const bNum = typeof bv === "number";
          if (aNum || bNum) {
            const an = aNum && Number.isFinite(av) ? (av as number) : -Infinity;
            const bn = bNum && Number.isFinite(bv) ? (bv as number) : -Infinity;
            return sort.dir === "asc" ? an - bn : bn - an;
          }
          const as = String(av ?? "").toLowerCase(); // allow-hardcode: comparador de sort (null→"" para localeCompare), no es config
          const bs = String(bv ?? "").toLowerCase(); // allow-hardcode: comparador de sort (null→"" para localeCompare), no es config
          return sort.dir === "asc"
            ? as.localeCompare(bs)
            : bs.localeCompare(as);
        });
      }
    }
    return result;
  }, [data, filter, sort, filterAccessor, sortAccessors]);

  return { filter, setFilter, sort, toggleSort, processedData };
}
